import React, { useEffect } from "react";

import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiDialog-paperFullScreen": {
    padding: theme.spacing(1),
    width: "95%",
    maxWidth: "95%",
    height: "90%",
    maxHeight: "90%",
  },
  "& .MuiDialog-paper": {
    overflow: "visible",
  },
}));

const checkKeyDown = (e) => {
  if (e.keyCode === 13) {
    e.preventDefault();
  }
};

const BootstrapDialogTitle = (props) => {
  const { children, onClose, handleClose, ...other } = props;

  const checkCloseModal = () => {
    if (onClose) {
      onClose()?.then((res) => {
        if (res) {
          handleClose();
        }
      });
    } else {
      handleClose();
    }
  };

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      <Tooltip title="Cerrar">
        <IconButton
          id="modalCloseBtn"
          aria-label="close"
          onClick={checkCloseModal}
          onKeyDown={checkKeyDown}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </Tooltip>
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func,
};

const CustomModal = ({
  isModalOpened,
  handleCloseModal,
  body,
  showBtnOpenModal = false,
  title = null,
  btnOpenAttributes = { variant: "", color: "primary" },
  okButtonAttributes = { variant: "contained", color: "success" },
  cancelButton = { variant: "contained", color: "warning" },
  callbackOk = null,
  callbackCancel = null,
  callbackOnClose = null, // se ejecuta cuando se cierre el modal
  id = "modal",
  showOk = false,
  showCancel = false,
  openText = "Abrir modal",
  buttonIcon = { name: "", position: "" },
  okText = "Aceptar",
  cancelText = "Cancelar",
  maxWidth = "sm",
  closeOnOk = true,
  closeOnCancel = true,
  fullScreen = false,
  fullWidth = false,
  onClose = null, // callback al presionar la x de cerrar en la esquina superior
  btnCancelIcon = <CancelIcon />,
  btnTooltipCancelar = "Cancelar y cerrar",
  btnTooltipAceptar = "Aceptar",
  titleStyle = null,
  imagen = null,
}) => {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (callbackOnClose) {
      callbackOnClose(true);
    }
    isModalOpened ? handleCloseModal(false) : setOpen(false);
  };

  const handleClickOk = (data) => {
    if (callbackOk) {
      callbackOk(data);
    }
    if (closeOnOk) {
      handleClose();
    }
  };

  const handleClickCancel = (event, reason) => {
    if (callbackCancel) {
      callbackCancel(event);
    }
    if (closeOnCancel && reason !== "backdropClick") {
      handleClose();
    }
    if (reason == "backdropClick") {
      handleClose();
    }
  };

  const getButtonIcon = () => {
    switch (buttonIcon.position) {
      case "end":
        return (
          <Button
            {...btnOpenAttributes}
            onClick={handleClickOpen}
            startIcon={openText}
            onKeyDown={checkKeyDown}
          >
            <Icon>{buttonIcon.name}</Icon>
          </Button>
        );
      case "start":
        return (
          <Button
            {...btnOpenAttributes}
            onClick={handleClickOpen}
            endIcon={openText}
            onKeyDown={checkKeyDown}
          >
            <Icon>{buttonIcon.name}</Icon>
          </Button>
        );
      default:
        return (
          <Button
            {...btnOpenAttributes}
            onClick={handleClickOpen}
            onKeyDown={checkKeyDown}
          >
            {openText}
          </Button>
        );
    }
  };

  return (
    <div>
      {showBtnOpenModal && getButtonIcon()}
      <BootstrapDialog
        onClose={(event, reason) => handleClickCancel(event, reason)}
        aria-labelledby={title}
        open={isModalOpened ?? open}
        fullScreen={fullScreen}
        fullWidth={fullWidth}
        maxWidth={maxWidth}
      >
        {title && (
          <BootstrapDialogTitle
            id={id}
            onClose={onClose ?? null}
            handleClose={handleClickCancel}
            sx={titleStyle}
          >{title != "Modal" && title}</BootstrapDialogTitle>
        )}

        <DialogContent dividers>{body}</DialogContent>
        <DialogActions>
          {showCancel ? (
            <Tooltip title={btnTooltipCancelar}>
              <Button
                id={id + "_btnCancel"}
                autoFocus
                variant={cancelButton.variant}
                color={cancelButton.color}
                onClick={handleClickCancel}
                onKeyDown={checkKeyDown}
              >
                {cancelText} {btnCancelIcon}
              </Button>
            </Tooltip>
          ) : (
            ""
          )}
          {showOk ? (
            <Tooltip title={btnTooltipAceptar}>
              <Button
                id={id + "_btnOk"}
                autoFocus
                variant={okButtonAttributes.variant}
                onClick={handleClickOk}
                color={okButtonAttributes.color}
                onKeyDown={checkKeyDown}
              >
                {okText}
              </Button>
            </Tooltip>
          ) : (
            ""
          )}
        </DialogActions> 
      </BootstrapDialog>
    </div>
  );
};

export default CustomModal;
